import { RegionCode } from '@kaa/api/customers';
import {
  AuthServiceAbstract,
  AuthStorageKeys,
  useAuthState,
} from '@kaa/auth/common';
import { useAsync, useAsyncCallback } from '@kaa/common/utils';
import { i18nKeys } from '@kaa/i18n/customers/keys';
import {
  Icon,
  SwBadge,
  SwButton,
  SwColumn,
  SwContainer,
  SwFetchErrorMessage,
  SwGrid,
  SwIcon,
  SwInfoTileSpotlight,
  SwLink,
  SwTitle,
} from '@kaa/ui-flanders/components';
import { navigate, RouteComponentProps } from '@reach/router';
import get from 'lodash.get';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { clearAuthDataBeforeLogin } from '@kaa/auth/common/auth.utils';
import { getConfig } from '../../common/config';
import { PageHeader } from '../components';
import {
  LOGIN_CANT_CONNECT_LINKS,
  LOGIN_CONNECTION_PROBLEMS_LINKS,
} from '../constants';
import { getPath, getRouterPath, Routes } from '../routes';
import { AuthContext } from '../types';
import './LoginScreen.style.scss';
import { OAuthErrorAlert } from './OAuthErrorAlert';

// How long the Login button stays disabled waiting for the page to leave.
const SIGNIN_NAVIGATION_TIMEOUT = 15000;

export const LoginScreen = (props: RouteComponentProps) => {
  const { t, i18n } = useTranslation();

  const config = getConfig();

  const { getUser } = useAuthState<AuthContext>();

  const { getDirectSigninRequestUrlToIdp, signinSilent } = useAuthState<
    AuthServiceAbstract
  >();

  const regionCode = get(config, 'buildConfig.regionCode') as RegionCode;

  const [
    { value: signinSilentResponse, loading: signinSilentLoading },
    getSigninSilent,
  ] = useAsyncCallback(async () => {
    return signinSilent();
  }, [signinSilent]);

  // The signin request must be created after clearAuthDataBeforeLogin(), or
  // the cleanup wipes the oidc.<state> entry this very request depends on.
  const [
    { loading: signinLoading, error: signinError },
    startSignin,
  ] = useAsyncCallback(async () => {
    clearAuthDataBeforeLogin();

    const url = await getDirectSigninRequestUrlToIdp();

    window.location.assign(`${url}&ui_locales=${i18n.language}`);

    // Hold signinLoading while the page leaves, so the button cannot be pressed
    // behind a navigation already under way. Bounded rather than permanent: if
    // the navigation is cancelled (Esc on a slow IDP) the button comes back
    // instead of staying dead until reload.
    await new Promise((resolve) => {
      setTimeout(resolve, SIGNIN_NAVIGATION_TIMEOUT);
    });
  }, [getDirectSigninRequestUrlToIdp, i18n.language]);

  const {
    loading: authLoading,
    value: isAuthenticated,
    error: isAuthenticatedError,
  } = useAsync(async () => getUser().then((authenticated) => authenticated), [
    getUser,
    config,
  ]);

  useEffect(() => {
    getSigninSilent();
  }, []);

  // Back from the IDP restores this page from the bfcache with its pre-login
  // state intact. Reload so signinSilent() runs again: the IDP session now
  // exists, so it forwards to the dashboard instead of stranding the user here.
  useEffect(() => {
    const reloadIfRestored = (event: PageTransitionEvent) => {
      if (event.persisted) {
        window.location.reload();
      }
    };

    window.addEventListener('pageshow', reloadIfRestored);
    return () => window.removeEventListener('pageshow', reloadIfRestored);
  }, []);

  useEffect(() => {
    if (isAuthenticated || signinSilentResponse) {
      const redirectionUri =
        sessionStorage.getItem(AuthStorageKeys.CALLBACK_REDIRECT_URI) ||
        getRouterPath(Routes.DASHBOARD);
      sessionStorage.removeItem(AuthStorageKeys.CALLBACK_REDIRECT_URI);

      // A callback error can be written and then silently rescued here before
      // OAuthErrorAlert ever mounts. Drop it, or it surfaces out of context on
      // some later visit to this screen.
      sessionStorage.removeItem(AuthStorageKeys.OAUTH_ERROR);

      navigate(redirectionUri);
    }
  }, [isAuthenticated, signinSilentResponse]);

  if (
    signinSilentLoading ||
    signinSilentResponse ||
    isAuthenticated ||
    authLoading
  ) {
    return <SwContainer loading />;
  }

  if (isAuthenticatedError || signinError) {
    return (
      <SwContainer error>
        <SwFetchErrorMessage
          onClick={() => {
            window.location.reload();
          }}
        />
      </SwContainer>
    );
  }

  return (
    <SwContainer>
      <SwGrid modStacked>
        <PageHeader title={t(i18nKeys.landing.title)} />
        <SwColumn width="10" widthS="12">
          <OAuthErrorAlert regionCode={regionCode} />

          <SwGrid modStacked className="vl-u-flex-v-flex-start">
            <SwColumn width="6" widthS="12">
              <SwInfoTileSpotlight
                tagName="div"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  width: '100%',
                  height: '100%',
                }}
                footer={
                  <SwButton modDisabled={signinLoading} onClick={startSignin}>
                    {t(i18nKeys.landing.cards.signin.cta)}
                  </SwButton>
                }
              >
                <SwBadge icon={Icon.BURGER_PROFILE} modMedium modAccent />
                <SwTitle tagName="h2" tagStyle="h4" className="vl-u-spacer">
                  {t(i18nKeys.navigation.landing)}
                </SwTitle>
                <p className="vl-u-spacer">
                  {t(i18nKeys.landing.cards.signin.description)}
                </p>
              </SwInfoTileSpotlight>
            </SwColumn>
            <SwColumn width="6" widthS="12">
              <SwInfoTileSpotlight
                tagName="div"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  width: '100%',
                  height: '100%',
                }}
                footer={
                  <SwButton modSecondary to={getPath(Routes.REGISTER)}>
                    {t(i18nKeys.landing.cards.signup.cta)}
                  </SwButton>
                }
              >
                <SwBadge icon={Icon.USER} modMedium modAccent />
                <SwTitle tagName="h2" tagStyle="h4" className="vl-u-spacer">
                  {t(i18nKeys.landing.cards.signup.title)}
                </SwTitle>
                <p className="vl-u-spacer">
                  {t(i18nKeys.landing.cards.signup.description)}
                </p>
              </SwInfoTileSpotlight>
            </SwColumn>
          </SwGrid>
        </SwColumn>
        <SwColumn width="10" widthS="12">
          <SwIcon
            icon={Icon.INFO_CIRCLE}
            className="vl-u-text--action"
            style={{ marginRight: '1rem' }}
          />
          <p className="vl-u-text--small" style={{ display: 'inline-block' }}>
            {t(i18nKeys.login.labels.usefullLinks.title)}
          </p>
          <ul style={{ marginLeft: '3rem', listStyle: 'initial' }}>
            <li>
              <SwLink
                to={t(LOGIN_CONNECTION_PROBLEMS_LINKS[regionCode])}
                target="_blank"
                className="vl-u-text--small"
              >
                {t(i18nKeys.login.labels.usefullLinks.howTo)}
              </SwLink>
            </li>
            <li>
              <SwLink
                to={t(LOGIN_CANT_CONNECT_LINKS[regionCode])}
                target="_blank"
                className="vl-u-text--small"
              >
                {t(i18nKeys.login.labels.usefullLinks.cantConnect)}
              </SwLink>
            </li>
          </ul>
        </SwColumn>
      </SwGrid>
    </SwContainer>
  );
};
